import { forwardRef, useMemo } from 'react';

import { Image, ImageProps } from './Image';

const IMAGE_CDN_BASE = `${import.meta.env.VITE_IMAGE_CDN_DOMAIN}/cdn-cgi/image/`;

export interface OptimizedImageProps extends ImageProps {
  quality?: number;
  format?: 'auto' | 'webp' | 'jpeg';
  fit?: 'scale-down' | 'contain' | 'cover' | 'crop' | 'pad';
}

/**
 * This component enhances the base Image component with optimization abilities.
 * Use the height, width, fit, quality, and format params to suit optimization to your needs.
 *
 * More details about these params can be found below.
 * https://developers.cloudflare.com/images/image-resizing/url-format/
 */
export const OptimizedImage = forwardRef<HTMLImageElement, OptimizedImageProps>(
  function OptimizedImage(
    { src, width, height, fit, quality, format = 'auto', ...rest }: OptimizedImageProps,
    ref
  ) {
    const imgSrc = useMemo(() => {
      if (src?.includes('localhost')) {
        return src;
      }

      const options = Object.entries({
        width,
        height,
        quality,
        format,
        fit,
      })
        .reduce((acc, curr) => {
          const [key, value] = curr;

          return value ? [...acc, `${key}=${value}`] : acc;
        }, [] as string[])
        .join(',');

      const cdnUrl = new URL(`${options}/${src}`, IMAGE_CDN_BASE);

      return cdnUrl.toString();
    }, [width, height, quality, format, fit, src]);

    return <Image ref={ref} src={imgSrc} {...rest} />;
  }
);
